import {Inject, Injectable, OnDestroy} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {ScrollingService} from 'projects/shared/scrolling/scrolling.service';
import {filter, first, map, take} from 'rxjs/operators';
import {ScrollingContainer} from '@shared/scrolling/scrolling-container.enum';
import {TokenStore} from './token.store';
import {TokenInstance} from '@paperlessio/sdk/api/models'
import {EditorRoutingService} from '@management/editor/editor-routing.service';
import {BlockStore} from '@blocks/block/block.store';
import {DOCUMENT} from '@angular/common';
import {totalOffsetTop} from '@paperlessio/sdk/util/helpers';

@Injectable()
export class TokenScrollService implements OnDestroy {

  public scrollToTokenInput = new Subject<string>();

  private lastScrolledTokenName: string;
  private lastScrolledToInstanceIndex = 0;

  private subs = new Subscription();

  constructor(
    private tokenStore: TokenStore,
    private scrollingService: ScrollingService,
    private blockStore: BlockStore,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Scroll to a given tioken instance by index
   * @param tokenName name of the token
   * @param containerName name of the container of the token instance
   * @param instanceIndex provied if the index of the instance is already known
   * @param editorRoutingService service to move in pages
   */
  public scrollToToken(
    tokenName: string,
    containerName: ScrollingContainer,
    instanceIndex?: number,
    editorRoutingService?: EditorRoutingService): void {
    this.tokenStore.getTokenByName(tokenName).pipe(
      map(token => {
        return {
          token,
          index: this.getInstanceIndex(tokenName, this.tokenStore.tokenInstances.get(token.name), instanceIndex)
        }
      }),
      map(async ({token, index}) => {
        const instance = this.tokenStore.tokenInstances.get(token.name)[index];
        const pageId = this.blockStore.getParentPageId(instance.blockId);
        let scrollIndex = index;

        if (!instance) {
          return;
        }

        this.lastScrolledTokenName = tokenName;
        this.lastScrolledToInstanceIndex = index;

        if (editorRoutingService && containerName === ScrollingContainer.editor) {
          if (pageId !== Number(editorRoutingService.selectedPage)) {
            await editorRoutingService.navigateToPage(pageId, 'tokens');
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        const pageInstances = this.tokenStore.getTokenInstancesByPage(tokenName).get(pageId);
        pageInstances.find((i, idx) => i.id === instance.id ? scrollIndex = idx: null);
        this.scroll(containerName, this.getInstanceElement(tokenName, scrollIndex));
      }), first()
    ).subscribe();
  }

  /**
   * Trigger scroll and save the id of the triggered scroll operation.
   * @param containerName container name of the DOM element
   * @param element the DOM element to which we want to scroll to
   */
  private scroll(containerName: ScrollingContainer, element: HTMLElement): void {
    const offset = totalOffsetTop(element);
    const scrollRequest = this.scrollingService
      .scrollToOffset(
        containerName,
        offset
      );

    this.subs.add(this.scrollingService.scrollFinish.pipe(
      filter(s => s.scrollId === scrollRequest.scrollId), take(1)).subscribe(i => {
        element?.scrollIntoView({block: 'nearest', behavior: 'smooth'})
        element?.classList.add('highlight');

        setTimeout(() => {
          element?.classList.remove('highlight');
        }, 500);
      }));
  }

  /**
   * Return the index of the selected token instance
   * @param instanceIndex if index is allready set otherwise null
   * @param tokenName given name of token
   * @param instances all living token instances
   */
  private getInstanceIndex(tokenName: string, instances: TokenInstance[], instanceIndex?: number): number {
    let index = 0;

    if (instanceIndex) {
      // When instanceIndex is set, stroll to it and ignore the other logic.
      index = instanceIndex;
    } else {
      // When instanceIndex isn't set, scroll through the token istances round-robin-style
      if (this.lastScrolledTokenName === tokenName) {
        index = this.lastScrolledToInstanceIndex;
        if (index > -1 && index < instances.length - 1) {
          index++;
        } else {
          index = 0;
        }
      } else {
        index = 0;
      }
    }

    // Safety measure to prevent an out of bounds error.
    index %= instances.length;

    return index;
  }

  /**
   * Returns the instance element to select by tokenName and index
   * @param tokenName the name of the token
   * @param index the index of the token instance
   */
  private getInstanceElement(
    tokenName: string,
    index: number): HTMLSpanElement {
    const elem = this.document.querySelectorAll<HTMLElement>
      ('.bucket-wrapper [title="' + tokenName + '"]')[index];

    return elem?.querySelector<HTMLSpanElement>('span');
  }
}
